import React, { useState } from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Icon } from "@UI/Components/Icon/Icon";
import { Stack } from "@UI/Components/Stack/Stack";
import { Avatar } from "@UI/Components/Avatar/Avatar";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import {
  SuperTalentBadge,
  SuperTalentBadgeWithWhiteBorder,
} from "@UI/Components/Icons/SuperTalentBadge";
import { Bell } from "@UI/Components/Icons/Bell";
import { Medal } from "@UI/Components/Icons/Medal";
import { Suitcase } from "@UI/Components/Icons/Suitcase";
import { TAdvantageLevel } from "@modules/ServiceProviders/services/serviceProviders";
import { SuperTalentCTA } from "../SuperTalent/SuperTalentCTA";
import { SupertTalentModal } from "../SuperTalent/SuperTalentModal";
import legacyMessages from "../messages";
import messages from "./messages";

interface IProps {
  advantageLevel: TAdvantageLevel;
  avatarSrc?: string;
  isFilterRequestBySuperTalentMetricsEnabled: boolean;
}

const bold = (...chunks: React.ReactNode[]) => (
  <Text as="span" fontWeight="700" color="gray.800">
    {chunks}
  </Text>
);

const SuperTalentAvatarBlock = ({ src }: { src?: string }) => (
  <Box position="relative" display="inline-block" flexShrink={0}>
    <Avatar src={src} w="42px" h="42px" border="2px solid #FF478F" />
    <Icon
      as={SuperTalentBadgeWithWhiteBorder}
      position="absolute"
      bottom="0"
      right="0"
      w="12px"
      h="12px"
    />
  </Box>
);

export const HeaderSection = ({
  advantageLevel,
  avatarSrc,
  isFilterRequestBySuperTalentMetricsEnabled,
}: IProps) => {
  const { generateRoute } = useRoutes();
  const { isMobile } = useMediaQuery();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Box flex={1}>
      {/* Hidden SVG gradient definition — referenced by perk icons below */}
      <Box as="svg" width={0} height={0} position="absolute" aria-hidden="true">
        <defs>
          <linearGradient id="perk-icon-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FF478F" />
            <stop offset="100%" stopColor="#FF9800" />
          </linearGradient>
        </defs>
      </Box>

      <Heading fontSize={{ base: "3xl", lg: "4xl" }} mb={3}>
        <Icon
          as={SuperTalentBadge}
          boxSize={{ base: "6", lg: "8" }}
          mr={2}
          display="inline"
          verticalAlign="middle"
        />
        <FormattedMessage {...messages.title} />
      </Heading>

      {isFilterRequestBySuperTalentMetricsEnabled ? (
        <>
          {advantageLevel === "super_talent" ? (
            <Flex align="center" gap={3} mb={4}>
              <SuperTalentAvatarBlock src={avatarSrc} />
              <Text color="gray.600" fontSize="md">
                <FormattedMessage {...messages.superTalentDescription} values={{ bold }} />
              </Text>
            </Flex>
          ) : (
            <Text color="gray.600" fontSize="md" mb={4}>
              <FormattedMessage {...messages.description} values={{ bold }} />
            </Text>
          )}

          <Stack spacing={3}>
            <Flex align="center" gap={3}>
              <Box flexShrink={0} sx={{ "& svg path": { fill: "url(#perk-icon-grad)" } }}>
                <Icon as={Bell} boxSize={5} />
              </Box>
              <Text fontSize="sm">
                <FormattedMessage {...messages.perkEarlyJobs} values={{ bold }} />
              </Text>
            </Flex>
            <Flex align="center" gap={3}>
              <Box flexShrink={0} sx={{ "& svg path": { fill: "url(#perk-icon-grad)" } }}>
                <Icon as={Medal} boxSize={5} />
              </Box>
              <Text fontSize="sm">
                <FormattedMessage {...messages.perkBadge} values={{ bold }} />
              </Text>
            </Flex>
            <Flex align="center" gap={3}>
              <Box flexShrink={0} sx={{ "& svg path": { fill: "url(#perk-icon-grad)" } }}>
                <Icon as={Suitcase} boxSize={5} />
              </Box>
              <Text fontSize="sm">
                <FormattedMessage {...messages.perkUnlimitedJobs} values={{ bold }} />
              </Text>
            </Flex>
          </Stack>
        </>
      ) : (
        <>
          <Text color="gray.600" fontSize={{ base: "md", md: "lg" }} mb={4}>
            <FormattedMessage {...legacyMessages.statusInfos} />
          </Text>

          <SuperTalentCTA onClick={() => setIsModalOpen(true)} />

          <SupertTalentModal
            isMobile={isMobile}
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            handleConfirm={() => generateRoute("SUPER_TALENT_URL")}
          />
        </>
      )}
    </Box>
  );
};
